import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { getStudentAvatar } from '../../shared/studentAvatar';

function AnimalStudentAvatar({ theme }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 92, sm: 104, md: 118 },
        height: { xs: 92, sm: 104, md: 118 },
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.92)',
        border: '3px solid rgba(255,255,255,0.96)',
        boxShadow: 'inset 0 -9px 0 rgba(15,23,42,0.06), 0 5px 12px rgba(15,23,42,0.14)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 8,
          borderRadius: '50%',
          bgcolor: theme.bgColor,
          opacity: 0.58,
        }}
      />
      <Box sx={{ position: 'absolute', left: '16%', top: '20%', color: '#facc15', fontSize: { xs: 16, md: 20 }, fontWeight: 1000 }}>✦</Box>
      <Box sx={{ position: 'absolute', right: '18%', top: '27%', color: '#facc15', fontSize: { xs: 12, md: 16 }, fontWeight: 1000 }}>✦</Box>
      <Box
        sx={{
          position: 'absolute',
          left: '18%',
          right: '18%',
          bottom: 6,
          height: 18,
          borderRadius: '50% 50% 0 0',
          bgcolor: '#84cc16',
          boxShadow: 'inset 0 5px 0 #bef264',
        }}
      />
      {theme.imageUrl ? <Box component="img" src={theme.imageUrl} alt="" loading="lazy" decoding="async"
        sx={{ position: 'relative', zIndex: 1, width: '84%', height: '84%', objectFit: 'contain' }} /> : <Typography
        component="span"
        sx={{
          position: 'relative',
          zIndex: 1,
          fontSize: { xs: 57, sm: 66, md: 76 },
          lineHeight: 1,
          filter: 'drop-shadow(0 4px 0 rgba(15,23,42,0.12))',
          transform: 'translateY(-2px)',
        }}
      >
        {theme.avatar}
      </Typography>}
    </Box>
  );
}

export default function StudentCard({ student, onClick, disabled = false }) {
  const theme = getStudentAvatar(student?.id, student?.avatar_id, student?.starter_emoji);
  const remaining = Number(student?.rounds_remaining ?? 3);
  const hasReading = student?.recommended_task?.task_type === 'weekly_reading';
  const onBreak = Boolean(student?.cooldown_until);
  const breakMinutes = Math.max(1, Math.ceil(Number(student?.cooldown_remaining_seconds || 0) / 60));
  const done = remaining <= 0 && !hasReading;
  const isBusy = disabled && !done;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled || done || onBreak}
      aria-label={onBreak ? `${student?.display_name || 'Öğrenci'}, mola zamanı, yaklaşık ${breakMinutes} dakika kaldı` : undefined}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: { xs: '1 / 1.02', sm: '1 / 0.94', md: '1 / 0.9' },
        minHeight: { xs: 184, sm: 196, md: 214 },
        borderRadius: 2,
        p: { xs: 1.1, md: 1.35 },
        bgcolor: onBreak ? '#eef2ff' : theme.bgColor,
        border: `3px solid ${onBreak ? '#a5b4fc' : theme.borderColor}`,
        boxShadow: '0 6px 0 rgba(15,23,42,0.08), 0 12px 22px rgba(15,23,42,0.08)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: done && !onBreak ? 0.58 : 1,
        overflow: 'hidden',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 5,
          borderRadius: 1.4,
          border: '2px solid rgba(255,255,255,0.72)',
          pointerEvents: 'none',
        },
        '&:hover': done
          ? undefined
          : {
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 0 rgba(15,23,42,0.08), 0 18px 28px rgba(79,70,229,0.18)',
            },
      }}
    >
      {(done || onBreak || isBusy || hasReading) && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            borderRadius: 99,
            bgcolor: onBreak ? '#e0e7ff' : done ? '#f8fafc' : hasReading ? '#dcfce7' : '#fff7ed',
            color: onBreak ? '#3730a3' : done ? '#64748b' : hasReading ? '#166534' : '#c2410c',
            px: 0.8,
            py: 0.25,
            fontSize: onBreak ? { xs: 13, md: 15 } : 11,
            fontWeight: 900,
          }}
        >
          {onBreak ? '🌙 Mola zamanı' : done ? 'Tamam' : hasReading ? 'Okuma hazır' : '...'}
        </Box>
      )}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', pt: { xs: 0.4, md: 0.6 }, zIndex: 1 }}>
        <AnimalStudentAvatar theme={theme} />
      </Box>
      <Typography
        sx={{
          color: '#242061',
          fontWeight: 1000,
          fontSize: { xs: 24, sm: 27, md: 31 },
          lineHeight: 1,
          textAlign: 'center',
          overflowWrap: 'anywhere',
          maxWidth: '100%',
          textShadow: '0 2px 0 rgba(255,255,255,0.82)',
          zIndex: 1,
        }}
      >
        {student?.display_name || 'Öğrenci'}
      </Typography>
      <Box sx={{ mt: 0.8, height: { xs: 24, md: 28 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', zIndex: 1 }}>
        {onBreak && (
          <Typography noWrap sx={{ color: '#4338ca', fontWeight: 800, fontSize: { xs: 14, md: 17 }, lineHeight: 1.2 }}>
            {breakMinutes} dk sonra
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}
